import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, X } from "lucide-react";
import { Button } from "./ui/button";
import { 
  detectAndRedirect, 
  saveLanguagePreference, 
  getBrowserLanguagePath,
  getLanguageNameFromPath,
  hasLanguagePreference
} from "../lib/languageDetection";
import { SUPPORTED_LANGUAGES } from "../locales/translations";

// Translations for the language suggestion banner
const bannerTranslations = {
  en: {
    message: "Would you like to view this site in",
    yes: "Yes, switch",
    no: "No, stay in English",
  },
  nl: {
    message: "Wilt u deze site bekijken in het",
    yes: "Ja, schakel over",
    no: "Nee, blijf in het Engels",
  },
  de: {
    message: "Möchten Sie diese Seite auf",
    yes: "Ja, wechseln",
    no: "Nein, auf Englisch bleiben",
  },
  da: {
    message: "Vil du se denne side på",
    yes: "Ja, skift",
    no: "Nej, bliv på engelsk",
  },
  "en-ca": {
    message: "Would you like to view our Canadian site?",
    yes: "Yes, switch",
    no: "No, stay here",
  },
};

export const LanguageRedirectHandler = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);
  const [suggestedPath, setSuggestedPath] = useState(null);
  const [suggestedLangName, setSuggestedLangName] = useState("");

  useEffect(() => {
    // Check if we should show a language suggestion
    if (!hasLanguagePreference()) {
      const suggested = getBrowserLanguagePath();
      const currentPath = location.pathname;
      
      // If suggested language is different from current and not default English
      if (suggested !== currentPath && suggested !== "/") {
        setSuggestedPath(suggested);
        setSuggestedLangName(getLanguageNameFromPath(suggested));
        setShowBanner(true);
      }
    }
  }, [location.pathname]);

  const handleAcceptLanguage = () => {
    if (suggestedPath) {
      saveLanguagePreference(suggestedPath);
      setShowBanner(false);
      navigate(suggestedPath);
    }
  };

  const handleDeclineLanguage = () => {
    saveLanguagePreference(location.pathname);
    setShowBanner(false);
  };

  const handleCloseBanner = () => {
    saveLanguagePreference(location.pathname);
    setShowBanner(false);
  };

  return (
    <>
      {children}
      
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[400px] z-50"
            data-testid="language-banner"
          >
            <div className="glass-strong rounded-2xl p-5 border border-white/10 shadow-2xl">
              <button
                onClick={handleCloseBanner}
                className="absolute top-3 right-3 text-[#52525B] hover:text-white transition-colors"
                data-testid="language-banner-close"
              >
                <X className="w-4 h-4" />
              </button>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#A855F7]/10 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5 text-[#A855F7]" />
                </div>
                
                <div className="flex-1">
                  <p className="text-sm text-white mb-3">
                    {bannerTranslations.en.message}{" "}
                    <span className="font-semibold text-[#D4AF37]">{suggestedLangName}</span>?
                  </p>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={handleAcceptLanguage}
                      className="bg-[#A855F7] hover:bg-[#9333EA] text-white text-xs rounded-full px-4 py-2"
                      data-testid="language-banner-accept"
                    >
                      {bannerTranslations.en.yes}
                    </Button>
                    <Button
                      onClick={handleDeclineLanguage}
                      variant="outline"
                      className="border-white/10 text-[#A1A1AA] hover:text-white text-xs rounded-full px-4 py-2"
                      data-testid="language-banner-decline"
                    >
                      {bannerTranslations.en.no}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LanguageRedirectHandler;
